import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronRight, Key, Check, X, Minus, FileWarning, Zap } from "lucide-react";
import { API } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";
import { toast } from "sonner";

// Accessible status indicator (#9): always shows a letter inside the dot.
// Tech-friendly even with low colour vision or bright sunlight.
const StatusDot = ({ status, size = "md", testid }) => {
  const map = {
    green:     { bg: "bg-green-500",  letter: "G", title: "Green — Pass" },
    amber:     { bg: "bg-amber-500",  letter: "A", title: "Amber — Advisory" },
    red:       { bg: "bg-red-500",    letter: "R", title: "Red — Fail" },
    blue:      { bg: "bg-blue-500",   letter: "B", title: "Blue — Not Visible" },
    dangerous: { bg: "bg-black",      letter: "D", title: "Dangerous" },
  };
  const cfg = map[status];
  const sz = size === "sm" ? "w-4 h-4 text-[8px]" : size === "lg" ? "w-6 h-6 text-xs" : "w-5 h-5 text-[10px]";
  if (!cfg) {
    return (
      <div
        className={`${sz} rounded-full bg-gray-300 flex items-center justify-center font-bold text-white`}
        title="Not assessed"
        data-testid={testid}
      >
        <span className="opacity-60">·</span>
      </div>
    );
  }
  return (
    <div
      className={`${sz} rounded-full ${cfg.bg} flex items-center justify-center font-bold text-white ring-1 ring-black/10`}
      title={cfg.title}
      data-testid={testid}
    >
      {cfg.letter}
    </div>
  );
};

const sectionNames = {
  in_car_checks: "In-Car Checks",
  under_bonnet: "Under-Bonnet",
  brakes_wheels: "Brakes & Wheels",
  tyres: "Tyres",
  suspension_steering: "Suspension & Steering",
  exhaust_underside: "Exhaust & Underside",
  fuel_system: "Fuel System",
  lights_electrical: "Lights & Electrical",
  final_checks: "Final Checks",
};

const SectionItemsPage = () => {
  const { id, sectionId } = useParams();
  const [items, setItems] = useState([]);
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingLockingKey, setSavingLockingKey] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  // MOT pre-fill hints (#17) — keyed by item_key (RECURRING advisories
  // from previous MOTs — historical context).
  const [motHintsByItem, setMotHintsByItem] = useState({});
  // iter204e — Current-visit MOT defects keyed by VI item_key. Sourced
  // from /api/jobs/{id}/mot-vi-flags (build_vi_flag_payload.by_item_key).
  // This is what the tester *just* flagged on the current MOT — the
  // tech needs to see it inline on the matching VI item card so the
  // capture happens in the same flow rather than them having to jump
  // back to the MOT workspace.
  const [motCurrentByItem, setMotCurrentByItem] = useState({});
  // iter212h (Feb 2026) — Battery test reuse candidate. Only relevant
  // on Under-Bonnet. Populated from
  // `GET /inspections/{id}/battery-reuse/candidate` when the current
  // inspection's battery item is untouched AND another inspection on
  // this job (or same reg within 24h) has a completed capture.
  const [batteryReuseCandidate, setBatteryReuseCandidate] = useState(null);
  const [batteryReuseApplying, setBatteryReuseApplying] = useState(false);
  const [batteryReuseDismissed, setBatteryReuseDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/jobs/${id}/inspection`).then(res => {
      if (!res.data) {
        navigate(`/app/tech/job/${id}/start`);
        return;
      }
      setInspection(res.data);
      axios.get(`${API}/inspections/${res.data.id}/sections/${sectionId}/items`).then(itemsRes => {
        setItems(itemsRes.data);
      });
      // iter212h — Only probe the reuse endpoint on Under-Bonnet.
      // Elsewhere it's dead weight and would 404 for jobs with no
      // battery item on the section anyway.
      if (sectionId === "under_bonnet") {
        axios
          .get(`${API}/inspections/${res.data.id}/battery-reuse/candidate`)
          .then(r => setBatteryReuseCandidate(r.data?.candidate || null))
          .catch(() => setBatteryReuseCandidate(null));
      }
    }).finally(() => setLoading(false));
    
    // Fetch MOT history hints (best-effort — fail silently)
    axios.get(`${API}/jobs/${id}/mot-vi-hints`).then(res => {
      setMotHintsByItem(res.data?.hints_by_item_key || {});
    }).catch(() => {});

    // iter204e — Current-visit MOT defects per VI item (best-effort).
    axios.get(`${API}/jobs/${id}/mot-vi-flags`).then(res => {
      setMotCurrentByItem(res.data?.by_item_key || {});
    }).catch(() => {});
  }, [id, sectionId, navigate]);

  // Quick save locking wheel key status
  const saveLockingKeyStatus = async (item, keyStatus) => {
    setSavingLockingKey(true);
    try {
      let status, lockingKeyPresent, lockingKeyLocation, notes;
      switch (keyStatus) {
        case "present": status = "green"; lockingKeyPresent = "yes"; lockingKeyLocation = "with_vehicle"; notes = null; break;
        case "not_located": status = "amber"; lockingKeyPresent = "no"; lockingKeyLocation = "not_found"; notes = "Locking wheel nut key not located in vehicle during inspection"; break;
        case "not_applicable": status = "blue"; lockingKeyPresent = "na"; lockingKeyLocation = null; notes = null; break;
        default: return;
      }
      await axios.put(`${API}/inspections/${inspection.id}/items/${item.id}`, {
        status, notes, locking_key_present: lockingKeyPresent, locking_key_location: lockingKeyLocation, locking_key_notes: null,
      });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, status, locking_key_present: lockingKeyPresent, locking_key_location: lockingKeyLocation } : i));
      const messages = { present: "Locking key present & located", not_located: "Locking key not located", not_applicable: "Standard nuts - not applicable" };
      toast.success(messages[keyStatus], { duration: 2000 });
    } catch (err) {
      console.error("Failed to save locking key status:", err);
      toast.error("Failed to save status");
    } finally {
      setSavingLockingKey(false);
    }
  };

  // ------- Bulk "Mark All Green" (Phase VI UX) ---------------------
  //
  // Design rules (do not weaken):
  //   * Never fabricate measurements, photos or structured values.
  //   * Never overwrite existing technician data: notes, photos,
  //     tester photo, measurement, or a status that is already set.
  //   * Skip only items where the GREEN verdict itself requires
  //     structured evidence:
  //       - `requires_measurement` (brake pad depth, tyre tread, wheel
  //         torque) — a green pass here still needs a captured value.
  //       - `requires_tester_photo` (battery / brake-fluid / coolant
  //         test) — the tester tool photo IS the pass evidence.
  //       - `is_battery_test` — battery tester result is the evidence.
  //     iter212e (Feb 2026): `requires_side_specification` used to
  //     block eligibility too, but that flag only matters when the
  //     item is being flagged AMBER/RED (the tester must say WHICH
  //     side is affected). A green verdict has no side to specify,
  //     so the entire Suspension & Steering section was hiding the
  //     "Mark All Green" button unnecessarily. Same for headlights,
  //     rear lights, fog lights. Removing it restores the button on
  //     every section that actually contains visual-check items.
  //   * The locking wheel key needs a dedicated three-way choice and
  //     is always excluded.
  //   * Idempotent — re-running does nothing on already-green items.
  //
  // Feedback distinguishes:
  //   * eligible items that just went green
  //   * items that still need mandatory evidence — presented as
  //     "still require measurements/photos", NEVER as an error.
  const isBulkGreenEligible = (item) => {
    // Untouched only. Existing green/red/amber/blue/dangerous stay.
    if (item.status !== null && item.status !== undefined) return false;
    // Locking wheel key uses its own three-way flow.
    if (item.item_key === "locking_wheel_key") return false;
    // Mandatory-evidence flags — a GREEN verdict still needs the
    // structured value / photo captured before we call it a pass.
    if (item.requires_measurement) return false;
    if (item.requires_tester_photo) return false;
    if (item.is_battery_test) return false;
    // iter212g (Feb 2026) — Air Conditioning Operation requires the
    // tech to complete the AC assessment AND declare the AC gas type
    // (R134a / R1234yf) so FOH can quote the correct service pack.
    // A bulk-green tap must not skip that capture step.
    if (item.requires_ac_gas_type) return false;
    // Any pre-existing technician data → treat as in-progress.
    if (item.notes && String(item.notes).trim() !== "") return false;
    if (Array.isArray(item.photo_urls) && item.photo_urls.length > 0) return false;
    if (item.tester_photo_url) return false;
    if (item.measurement_value !== null && item.measurement_value !== undefined) return false;
    // Historical MOT advisory on this item — technician judgement needed.
    if ((motHintsByItem[item.item_key] || []).length > 0) return false;
    return true;
  };

  // iter212f (Feb 2026) — Section-level exclusion. Two sections have
  // NO items where a bulk green pass is meaningful; the button is
  // hidden entirely so techs never see it there.
  //   * `tyres` — every position needs tread depth (mm), % worn,
  //     brand + spec capture. No item passes on eyeball alone.
  //   * `final_checks` — wheel torque, tyre pressure sheet, road-test
  //     confirmation, service-indicator reset. All bespoke capture.
  const BULK_GREEN_EXCLUDED_SECTIONS = new Set(["tyres", "final_checks"]);
  const isBulkGreenEnabledForSection = !BULK_GREEN_EXCLUDED_SECTIONS.has(sectionId);

  // "Needs evidence" list — untouched AND not eligible for bulk
  // green. Returned as `{count, labels}` so the toast can name the
  // items the tech still needs to visit (much clearer than a raw
  // count on Under-Bonnet where 3 tester-tool items are legitimate
  // skips).
  const evidenceStillNeeded = () => {
    const remaining = items.filter(i =>
      (i.status === null || i.status === undefined) && !isBulkGreenEligible(i)
    );
    return {
      count: remaining.length,
      labels: remaining
        .map(i => i.item_label || i.label || i.item_key)
        .filter(Boolean),
    };
  };

  const bulkMarkGreen = async () => {
    const eligible = items.filter(isBulkGreenEligible);
    const stillPre = evidenceStillNeeded();

    if (eligible.length === 0) {
      if (stillPre.count > 0) {
        toast.info(
          stillPre.labels.length > 0
            ? `No items eligible for bulk green — still need capture on: ${stillPre.labels.join(", ")}.`
            : `No items eligible for bulk green — ${stillPre.count} item(s) still require measurements/photos.`
        );
      } else {
        toast.info("Nothing to mark — every item in this section is already complete.");
      }
      return;
    }
    if (!window.confirm(
      `Mark ${eligible.length} eligible item(s) as GREEN?\n\n` +
      (stillPre.count > 0
        ? `${stillPre.count} item(s) still require capture and will be left for you: ${stillPre.labels.join(", ")}.`
        : `Every unset item in this section will be marked green.`)
    )) return;

    setBulkSaving(true);
    // iter212e — Run PUTs in parallel so a large section (Suspension
    // & Steering has 11 items) doesn't take 11 sequential round-trips
    // to complete. Each PUT keeps its own success/failure track so we
    // can flip only the successfully-persisted items in local state.
    const results = await Promise.all(eligible.map(async (it) => {
      try {
        await axios.put(`${API}/inspections/${inspection.id}/items/${it.id}`, {
          status: "green",
        });
        return { id: it.id, ok: true };
      } catch (err) {
        console.error("Bulk save failed for", it.item_key, err);
        return { id: it.id, ok: false };
      }
    }));
    const ok = results.filter(r => r.ok).length;
    const hardErrors = results.length - ok;
    const succeededIds = new Set(results.filter(r => r.ok).map(r => r.id));
    setItems(prev => prev.map(i => (
      succeededIds.has(i.id) ? { ...i, status: "green" } : i
    )));
    setBulkSaving(false);

    const stillPost = evidenceStillNeeded();
    if (hardErrors > 0) {
      toast.error(`${ok} item(s) marked green. ${hardErrors} item(s) failed to save — please retry.`, { duration: 4000 });
    } else if (stillPost.count === 0) {
      toast.success("All eligible items marked green.", { duration: 2500 });
    } else {
      toast.success(
        `${ok} items marked green. Still need capture: ${stillPost.labels.join(", ")}.`,
        { duration: 5000 }
      );
    }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  const allComplete = items.every(item => item.status !== null);
  // Show the bulk-green button whenever the section is enabled AND at
  // least ONE untouched item is eligible under isBulkGreenEligible.
  // This reads the same rules the handler uses, so the button count
  // and the feedback message are guaranteed to agree.
  const unsetEligibleCount = isBulkGreenEnabledForSection
    ? items.filter(isBulkGreenEligible).length
    : 0;

  const isLockingWheelKeyItem = (item) => item.item_key === "locking_wheel_key";

  // iter212h — Human-readable "12 min ago" for the battery reuse banner.
  const formatMinutesAgo = (iso) => {
    if (!iso) return "recently";
    const then = new Date(iso).getTime();
    if (!then) return "recently";
    const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
    if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
    const days = Math.round(hrs / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  };

  const applyBatteryReuse = async () => {
    if (!batteryReuseCandidate || !inspection) return;
    setBatteryReuseApplying(true);
    try {
      await axios.post(
        `${API}/inspections/${inspection.id}/battery-reuse/${batteryReuseCandidate.source_inspection_id}`
      );
      // Refresh the items list so the battery card flips to its
      // reused status + values.
      const itemsRes = await axios.get(`${API}/inspections/${inspection.id}/sections/${sectionId}/items`);
      setItems(itemsRes.data);
      setBatteryReuseCandidate(null);
      toast.success(
        `Battery result reused from ${batteryReuseCandidate.source_inspection_type}. Tap the card to re-test if needed.`,
        { duration: 4500 }
      );
    } catch (err) {
      console.error("Battery reuse failed:", err);
      toast.error("Could not reuse battery result — please re-test.");
    } finally {
      setBatteryReuseApplying(false);
    }
  };

  const showBatteryReuseBanner =
    sectionId === "under_bonnet" &&
    !batteryReuseDismissed &&
    batteryReuseCandidate &&
    batteryReuseCandidate.source_inspection_id;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="p-4 max-w-2xl mx-auto">
        <Button
          variant="ghost"
          className="mb-2"
          onClick={() => navigate(`/app/tech/job/${id}/sections`)}
          data-testid="back-to-sections-btn"
        >
          ← Back to Sections
        </Button>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold" data-testid="section-items-title">{sectionNames[sectionId] || sectionId}</h2>
          {unsetEligibleCount >= 1 && (
            <Button
              size="sm"
              variant="outline"
              className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50"
              onClick={bulkMarkGreen}
              disabled={bulkSaving}
              data-testid="bulk-mark-green-btn"
            >
              <Zap className="w-3.5 h-3.5" />
              Mark {unsetEligibleCount} green
            </Button>
          )}
        </div>

        {showBatteryReuseBanner && (
          <div
            className="mb-4 rounded-lg border border-sky-300 bg-sky-50 p-3 flex flex-col gap-3"
            data-testid="battery-reuse-banner"
          >
            <div className="flex items-start gap-2">
              <Zap className="w-4 h-4 text-sky-700 mt-0.5 shrink-0" />
              <div className="text-sm text-sky-900 leading-snug">
                <div className="font-semibold">
                  Battery test already completed on this vehicle
                </div>
                <div className="text-sky-800">
                  Captured {formatMinutesAgo(batteryReuseCandidate.source_captured_at)} in <span className="font-medium">{batteryReuseCandidate.source_inspection_type}</span>
                  {batteryReuseCandidate.source_tester_name
                    ? <> by <span className="font-medium">{batteryReuseCandidate.source_tester_name}</span></>
                    : null}
                  .
                </div>
                {batteryReuseCandidate.preview && (
                  <div className="mt-1 text-xs text-sky-800">
                    {batteryReuseCandidate.preview.battery_voltage_v ? `${batteryReuseCandidate.preview.battery_voltage_v} V` : null}
                    {batteryReuseCandidate.preview.measured_cca_a ? ` · ${batteryReuseCandidate.preview.measured_cca_a} CCA` : null}
                    {batteryReuseCandidate.preview.health_percent ? ` · ${batteryReuseCandidate.preview.health_percent}% health` : null}
                    {batteryReuseCandidate.preview.status ? ` · status ${batteryReuseCandidate.preview.status.toUpperCase()}` : null}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-sky-600 hover:bg-sky-700 text-white"
                onClick={applyBatteryReuse}
                disabled={batteryReuseApplying}
                data-testid="battery-reuse-accept-btn"
              >
                {batteryReuseApplying ? "Reusing…" : "Reuse results"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-sky-300 text-sky-800 hover:bg-sky-100"
                onClick={() => setBatteryReuseDismissed(true)}
                disabled={batteryReuseApplying}
                data-testid="battery-reuse-retest-btn"
              >
                Re-test
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2 mb-6">
          {items.map(item => {
            const motHints = motHintsByItem[item.item_key] || [];
            const hasMotHint = motHints.length > 0;
            // iter204e — Current-visit MOT defects for this item key.
            const motCurrent = motCurrentByItem[item.item_key] || {};
            const motCurrentDefects = motCurrent.defects || [];
            const motCurrentCounts  = motCurrent.counts  || { terminal: 0, advisory: 0, total: 0 };
            const hasMotCurrent     = motCurrentDefects.length > 0;
            const hasMotTerminal    = (motCurrentCounts.terminal || 0) > 0;
            if (isLockingWheelKeyItem(item)) {
              return (
                <Card key={item.id} className="border-2 border-slate-300" data-testid={`item-card-${item.item_key}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Key className="w-5 h-5 text-slate-600" />
                      <span className="font-medium">{item.item_label}</span>
                      {item.status && <StatusDot status={item.status} size="sm" testid={`item-status-${item.item_key}`} />}
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => saveLockingKeyStatus(item, "present")} disabled={savingLockingKey}
                        className={`flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-all
                          ${item.locking_key_present === "yes" ? "bg-green-500 border-green-600 text-white" : "bg-green-50 border-green-300 text-green-700 hover:bg-green-100"}
                          disabled:opacity-50`} data-testid="locking-key-present-btn">
                        <Check className="w-3.5 h-3.5" /><span>Located</span>
                      </button>
                      <button onClick={() => saveLockingKeyStatus(item, "not_located")} disabled={savingLockingKey}
                        className={`flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-all
                          ${item.locking_key_present === "no" ? "bg-red-500 border-red-600 text-white" : "bg-red-50 border-red-300 text-red-700 hover:bg-red-100"}
                          disabled:opacity-50`} data-testid="locking-key-not-located-btn">
                        <X className="w-3.5 h-3.5" /><span>Not Located</span>
                      </button>
                      <button onClick={() => saveLockingKeyStatus(item, "not_applicable")} disabled={savingLockingKey}
                        className={`flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-all
                          ${item.locking_key_present === "na" ? "bg-blue-500 border-blue-600 text-white" : "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"}
                          disabled:opacity-50`} data-testid="locking-key-na-btn">
                        <Minus className="w-3.5 h-3.5" /><span>N/A</span>
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            }
            return (
              <Card
                key={item.id}
                className={`cursor-pointer hover:shadow-md transition-shadow ${
                  hasMotTerminal
                    ? "ring-2 ring-red-400"
                    : hasMotCurrent
                      ? "ring-2 ring-orange-300"
                      : hasMotHint
                        ? "ring-1 ring-amber-300"
                        : ""
                }`}
                onClick={() => navigate(`/app/tech/job/${id}/item/${item.id}`, { state: { inspectionId: inspection.id } })}
                data-testid={`item-card-${item.item_key}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <StatusDot status={item.status} testid={`item-status-${item.item_key}`} />
                      <span>{item.item_label}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  {/* iter204e — Current-visit MOT defects. Shown FIRST
                      (above any history) because the tech needs to
                      see what the tester just flagged on THIS MOT. */}
                  {hasMotCurrent && (
                    <div
                      className={`mt-2 ml-8 rounded-md border px-2 py-1.5 text-xs space-y-1 ${
                        hasMotTerminal
                          ? "border-red-300 bg-red-50 text-red-800"
                          : "border-orange-300 bg-orange-50 text-orange-800"
                      }`}
                      data-testid={`mot-current-${item.item_key}`}
                    >
                      <div className="flex items-center gap-1.5 font-semibold">
                        <Zap className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>
                          MOT&nbsp;now ({motCurrentCounts.total})
                          {motCurrentCounts.terminal > 0 && (
                            <span className="ml-1 px-1 py-0 rounded bg-red-600 text-white text-[9.5px] font-black uppercase tracking-wider">
                              {motCurrentCounts.terminal} fail
                            </span>
                          )}
                          {motCurrentCounts.advisory > 0 && (
                            <span className={`ml-1 px-1 py-0 rounded text-[9.5px] font-black uppercase tracking-wider ${
                              hasMotTerminal
                                ? "bg-red-200 text-red-900"
                                : "bg-amber-400 text-amber-900"
                            }`}>
                              {motCurrentCounts.advisory} advisory
                            </span>
                          )}
                        </span>
                      </div>
                      <ul className="space-y-0.5 leading-tight">
                        {motCurrentDefects.slice(0, 3).map((d, i) => (
                          <li
                            key={`${d.text || ""}-${i}`}
                            className="flex items-start gap-1.5"
                            data-testid={`mot-current-defect-${item.item_key}-${i}`}
                          >
                            <span className={`shrink-0 mt-0.5 inline-block w-1 h-1 rounded-full ${
                              d.is_terminal ? "bg-red-600" : "bg-orange-500"
                            }`} />
                            <span className={d.is_terminal ? "font-medium" : ""}>
                              {d.severity_label}: &ldquo;{(d.text || "").slice(0, 90)}{(d.text || "").length > 90 ? "…" : ""}&rdquo;
                            </span>
                          </li>
                        ))}
                        {motCurrentDefects.length > 3 && (
                          <li className="italic opacity-80">+{motCurrentDefects.length - 3} more — tap to view</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {hasMotHint && (
                    <div
                      className="mt-2 ml-8 flex items-start gap-1.5 text-xs text-amber-700"
                      data-testid={`mot-hint-${item.item_key}`}
                    >
                      <FileWarning className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        <span className="font-semibold">MOT history ({motHints.length}):</span>{" "}
                        {motHints.slice(0, 2).map(h => `"${(h.text || "").slice(0, 60)}"`).join(" · ")}
                        {motHints.length > 2 && ` (+${motHints.length - 2} more)`}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Button
          className="w-full"
          variant={allComplete ? "default" : "outline"}
          onClick={() => navigate(`/app/tech/job/${id}/sections`)}
          data-testid="back-to-sections-list-btn"
        >
          {allComplete ? "Section Complete ✓ Back to Sections" : "Back to Sections"}
        </Button>
      </div>
    </div>
  );
};

export default SectionItemsPage;
